import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import AddAlertIcon from "@material-ui/icons/AddAlert"
import CollectionsIcon from "@material-ui/icons/Collections"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects"
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline"
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline"
import SaveIcon from "@material-ui/icons/Save"
import VisibilityIcon from "@material-ui/icons/Visibility"
import React, { useEffect, useRef, useState } from "react"
import {
    AlertaPainel,
    excluirAlertaPainel,
    formatarDataHora,
    formatarTempoRestante,
    gerarAlertaPainelIA,
    listarAlertasPainel,
    MANTER_VALIDADE,
    NovoAlertaPainel,
    pausarAlertaPainel,
    PAUSAS_ALERTA,
    reativarAlertaPainel,
    salvarAlertaPainel,
    VALIDADES_ALERTA,
} from "../../../services/alertasPainel"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { useSnackbar } from "../../../utils/notificacoes"
import DialogConfirmacao from "../../Cardapio/componentes/DialogConfirmacao"
import DialogGaleriaFotos from "../../Cardapio/componentes/DialogGaleriaFotos"
import EditorHtml from "../../components/EditorHtml"

const ROTA_GALERIA = "/servicos/ferramenta/galeria"

const CORES_SITUACAO: Record<AlertaPainel["situacao"], { fundo: string; texto: string }> = {
    ATIVO: { fundo: "#e8f5e9", texto: "#2e7d32" },
    PAUSADO: { fundo: "#fff3e0", texto: "#e65100" },
    EXPIRADO: { fundo: "#eceff1", texto: "#546e7a" },
}

const formularioVazio: NovoAlertaPainel = {
    ordem: 1,
    validade: 86400,
    conteudo: "",
}

/**
 * Cadastro dos avisos exibidos no sino do painel.
 *
 * Réplica da tela Tapestry CadastroAlertasPainel do sistema administrativo. Os alertas
 * ficam na tabela AlertaPainel e o Redis guarda só a lista dos visíveis. A validade vira
 * uma data de expiração; pausar tira o alerta do painel sem excluí-lo (por um tempo ou até
 * reativar). Restrita à equipe Cliente Fiel; o backend confere o perfil, não só o menu.
 */
export function AlertasPainelForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [alertas, setAlertas] = useState<AlertaPainel[]>([])
    const [formulario, setFormulario] = useState<NovoAlertaPainel>(formularioVazio)
    const [editandoId, setEditandoId] = useState<number | null>(null)
    const [salvando, setSalvando] = useState(false)
    const [galeriaAberta, setGaleriaAberta] = useState(false)

    const configuracoes = useAppStore(s => s.configuracoes)
    const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id

    // O editor pede a imagem e espera a resposta; guardamos o resolve da promessa até
    // o usuário escolher uma foto ou fechar a galeria.
    const resolverImagem = useRef<((url: string | null) => void) | null>(null)

    const selecionarImagem = () =>
        new Promise<string | null>(resolve => {
            resolverImagem.current = resolve
            setGaleriaAberta(true)
        })

    const fecharGaleria = (url: string | null) => {
        setGaleriaAberta(false)
        resolverImagem.current?.(url)
        resolverImagem.current = null
    }

    const [dialogIA, setDialogIA] = useState(false)
    const [descricaoIA, setDescricaoIA] = useState("")
    const [gerandoIA, setGerandoIA] = useState(false)
    const [visualizando, setVisualizando] = useState<AlertaPainel | null>(null)
    // Confirmação em diálogo próprio, e não window.confirm: o confirm nativo é suprimido
    // em parte dos contextos onde o painel roda, e ali o excluir simplesmente não acontecia.
    const [alertaParaExcluir, setAlertaParaExcluir] = useState<AlertaPainel | null>(null)
    const [alertaParaPausar, setAlertaParaPausar] = useState<AlertaPainel | null>(null)
    const [duracaoPausa, setDuracaoPausa] = useState<number>(0)

    const gerarComIA = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao || !descricaoIA.trim()) return

        setGerandoIA(true)
        try {
            const res = await gerarAlertaPainelIA(autorizacao, descricaoIA.trim())
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Não foi possível gerar o alerta.", "error")
                return
            }
            // Substitui o conteúdo: o HTML gerado é um bloco fechado, e concatenar com o que
            // já estava escrito produziria markup aninhado sem sentido.
            setFormulario(prev => ({ ...prev, conteudo: res.html }))
            setDialogIA(false)
            setDescricaoIA("")
            alertStart("Alerta gerado. Revise antes de salvar.", "success")
        } finally {
            setGerandoIA(false)
        }
    }

    const alertStart = (msg: string, tipo: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(msg, { variant: tipo })

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await listarAlertasPainel(autorizacao)
            if ("retornoErro" in res) alertStart(res.mensagem || "Erro ao carregar", "error")
            // O backend já ordena por "ordem"; a ordenação aqui é só uma garantia.
            // Alertas sem ordem definida vão para o fim.
            else
                setAlertas(
                    [...res].sort(
                        (a, b) =>
                            (a.ordem ?? Number.MAX_SAFE_INTEGER) -
                            (b.ordem ?? Number.MAX_SAFE_INTEGER)
                    )
                )
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const limparFormulario = () => {
        setFormulario(formularioVazio)
        setEditandoId(null)
    }

    const editar = (alerta: AlertaPainel) => {
        setEditandoId(alerta.id)
        setFormulario({
            id: alerta.id,
            ordem: alerta.ordem ?? 1,
            // Por padrão a edição preserva a data de expiração já cadastrada.
            validade: MANTER_VALIDADE,
            conteudo: alerta.conteudo ?? "",
        })
        window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const salvar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        const textoPuro = formulario.conteudo.replace(/<[^>]*>/g, "").trim()
        if (!textoPuro) {
            alertStart("Informe o conteúdo do alerta.", "error")
            return
        }
        if (!Number.isFinite(Number(formulario.ordem))) {
            alertStart("Informe a ordem de exibição.", "error")
            return
        }

        setSalvando(true)
        setLoading(true)
        try {
            const res = await salvarAlertaPainel(autorizacao, {
                ...formulario,
                ordem: Number(formulario.ordem),
            })
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar", "error")
                return
            }
            alertStart("Alerta salvo com sucesso.", "success")
            limparFormulario()
            await carregar()
        } finally {
            setSalvando(false)
            setLoading(false)
        }
    }

    const excluir = async (alerta: AlertaPainel) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setAlertaParaExcluir(null)

        setLoading(true)
        try {
            const res = await excluirAlertaPainel(autorizacao, alerta.id)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao excluir", "error")
                return
            }
            alertStart("Alerta removido.", "success")
            if (editandoId === alerta.id) limparFormulario()
            await carregar()
        } finally {
            setLoading(false)
        }
    }

    const abrirPausa = (alerta: AlertaPainel) => {
        setDuracaoPausa(0)
        setAlertaParaPausar(alerta)
    }

    const pausar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        const alerta = alertaParaPausar
        if (!autorizacao || !alerta) return
        setAlertaParaPausar(null)

        setLoading(true)
        try {
            const res = await pausarAlertaPainel(autorizacao, alerta.id, duracaoPausa)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao pausar", "error")
                return
            }
            alertStart(
                duracaoPausa > 0
                    ? `Alerta pausado até ${formatarDataHora(res.pausadoAte)}.`
                    : "Alerta pausado até ser reativado.",
                "success"
            )
            await carregar()
        } finally {
            setLoading(false)
        }
    }

    const reativar = async (alerta: AlertaPainel) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return

        setLoading(true)
        try {
            const res = await reativarAlertaPainel(autorizacao, alerta.id)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao reativar", "error")
                return
            }
            alertStart("Alerta reativado.", "success")
            await carregar()
        } finally {
            setLoading(false)
        }
    }

    const rotuloSituacao = (alerta: AlertaPainel) => {
        if (alerta.situacao === "EXPIRADO") return "Expirado"
        if (alerta.situacao === "PAUSADO")
            return alerta.ativo && alerta.pausadoAte
                ? `Pausado até ${formatarDataHora(alerta.pausadoAte)}`
                : "Pausado"
        return "Ativo"
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Alertas do painel
                </Typography>
                <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 16 }}>
                    Avisos exibidos no sino de notificações do painel de pedidos, para todos os
                    estabelecimentos.
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <AddAlertIcon style={{ color: "#4caf50", marginRight: 8 }} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            {editandoId ? "Editar alerta" : "Novo alerta"}
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Ordem de exibição"
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={formulario.ordem}
                                    onChange={e =>
                                        setFormulario(prev => ({
                                            ...prev,
                                            ordem: e.target.value as unknown as number,
                                        }))
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel>Validade</InputLabel>
                                    <Select
                                        label="Validade"
                                        value={formulario.validade}
                                        onChange={e =>
                                            setFormulario(prev => ({
                                                ...prev,
                                                validade: Number(e.target.value),
                                            }))
                                        }
                                    >
                                        {editandoId && (
                                            <MenuItem value={MANTER_VALIDADE}>
                                                Manter a atual
                                            </MenuItem>
                                        )}
                                        {VALIDADES_ALERTA.map(v => (
                                            <MenuItem key={v.valor} value={v.valor}>
                                                {v.rotulo}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <EditorHtml
                                    label="Conteúdo"
                                    value={formulario.conteudo}
                                    onChange={html =>
                                        setFormulario(prev => ({ ...prev, conteudo: html }))
                                    }
                                    altura={200}
                                    placeholder="Escreva o aviso que aparecerá no painel…"
                                    selecionarImagem={selecionarImagem}
                                />
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    mt={1}
                                >
                                    <Box display="flex" alignItems="center">
                                        <CollectionsIcon
                                            fontSize="small"
                                            style={{ color: "#1565c0", marginRight: 6 }}
                                        />
                                        <Link
                                            href={ROTA_GALERIA}
                                            // Nova aba para não perder o alerta que está sendo escrito.
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            // Azul de link em vez do primário do tema (vermelho do
                                            // estabelecimento), que aqui competia com o botão Salvar.
                                            style={{
                                                color: "#1565c0",
                                                textDecoration: "underline",
                                            }}
                                        >
                                            Enviar novas imagens para a galeria
                                        </Link>
                                    </Box>
                                    <Button
                                        size="small"
                                        startIcon={<EmojiObjectsIcon />}
                                        onClick={() => setDialogIA(true)}
                                    >
                                        Gerar com IA
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box display="flex" justifyContent="flex-end" gridGap={8} mt={2}>
                            {editandoId && (
                                <Button variant="outlined" onClick={limparFormulario}>
                                    Cancelar edição
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                disabled={salvando}
                                onClick={salvar}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Alertas cadastrados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ordem</TableCell>
                                    <TableCell>Situação</TableCell>
                                    <TableCell>Conteúdo</TableCell>
                                    <TableCell>Expira em</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {alertas.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5}>Nenhum alerta cadastrado.</TableCell>
                                    </TableRow>
                                )}
                                {alertas.map(alerta => (
                                    <TableRow
                                        key={alerta.id}
                                        // Pausados/expirados esmaecidos: não estão aparecendo no painel.
                                        style={{ opacity: alerta.situacao === "ATIVO" ? 1 : 0.6 }}
                                    >
                                        <TableCell>{alerta.ordem ?? "—"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={rotuloSituacao(alerta)}
                                                style={{
                                                    backgroundColor:
                                                        CORES_SITUACAO[alerta.situacao]?.fundo,
                                                    color: CORES_SITUACAO[alerta.situacao]?.texto,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {/* Mesmo tratamento que o sino de avisos dá ao alerta:
                                                o conteúdo é HTML escrito pela equipe Cliente Fiel. */}
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: alerta.conteudo || "",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {alerta.dataExpiracao ? (
                                                <Tooltip
                                                    title={formatarTempoRestante(
                                                        alerta.segundosRestantes
                                                    )}
                                                >
                                                    <span>
                                                        {formatarDataHora(alerta.dataExpiracao)}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                "Sem expiração"
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Ver alerta">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setVisualizando(alerta)}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => editar(alerta)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {alerta.situacao === "PAUSADO" ? (
                                                <Tooltip title="Reativar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => reativar(alerta)}
                                                    >
                                                        <PlayCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Pausar">
                                                    {/* span: o Tooltip não escuta botão desabilitado */}
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={
                                                                alerta.situacao === "EXPIRADO"
                                                            }
                                                            onClick={() => abrirPausa(alerta)}
                                                        >
                                                            <PauseCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Excluir">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setAlertaParaExcluir(alerta)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>

                <DialogGaleriaFotos
                    open={galeriaAberta}
                    idEstabelecimento={idEstabelecimento}
                    onSelect={(url: string) => fecharGaleria(url)}
                    onClose={() => fecharGaleria(null)}
                />

                <DialogConfirmacao
                    open={!!alertaParaExcluir}
                    mensagem="Tem certeza que deseja excluir este alerta do painel?"
                    onCancelar={() => setAlertaParaExcluir(null)}
                    onConfirmar={() => alertaParaExcluir && excluir(alertaParaExcluir)}
                />

                <Dialog
                    open={!!alertaParaPausar}
                    onClose={() => setAlertaParaPausar(null)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Pausar alerta</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 16 }}>
                            O alerta deixa de aparecer no painel, mas continua cadastrado. A
                            validade continua contando durante a pausa.
                        </Typography>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Pausar por</InputLabel>
                            <Select
                                label="Pausar por"
                                value={duracaoPausa}
                                onChange={e => setDuracaoPausa(Number(e.target.value))}
                            >
                                {PAUSAS_ALERTA.map(p => (
                                    <MenuItem key={p.valor} value={p.valor}>
                                        {p.rotulo}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAlertaParaPausar(null)}>Cancelar</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PauseCircleOutlineIcon />}
                            onClick={pausar}
                        >
                            Pausar
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={dialogIA}
                    onClose={() => !gerandoIA && setDialogIA(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Gerar alerta com IA</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 12 }}>
                            Descreva o aviso que você quer mostrar. A IA monta o HTML e joga no
                            campo de conteúdo, onde você ainda pode editar antes de salvar.
                        </Typography>
                        <TextField
                            autoFocus
                            multiline
                            fullWidth
                            minRows={4}
                            variant="outlined"
                            placeholder="Ex.: avisar que no domingo das 2h às 5h o sistema fica indisponível para manutenção"
                            value={descricaoIA}
                            onChange={e => setDescricaoIA(e.target.value)}
                            disabled={gerandoIA}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogIA(false)} disabled={gerandoIA}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={gerarComIA}
                            disabled={gerandoIA || !descricaoIA.trim()}
                            startIcon={
                                gerandoIA ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <EmojiObjectsIcon />
                                )
                            }
                        >
                            {gerandoIA ? "Gerando…" : "Gerar"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={!!visualizando}
                    onClose={() => setVisualizando(null)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Alerta cadastrado</DialogTitle>
                    <DialogContent dividers>
                        {/* Mesmo tratamento do sino de avisos: o alerta é exibido como HTML. */}
                        <div dangerouslySetInnerHTML={{ __html: visualizando?.conteudo || "" }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setVisualizando(null)}>Fechar</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    )
}
